"use client";

import { useState, type ReactNode } from "react";
import { PlayerCell } from "@/components/casino/LiveStats";
import { PlayerStatsModal } from "@/components/casino/PlayerStatsModal";
import { Icon } from "@/components/ui/Icon";
import { Avatar, Chip, Panel } from "@/components/ui/primitives";
import { RowsSkeleton, SectionEmpty, SectionError } from "@/components/ui/states";
import { cn } from "@/lib/cn";

/* One board, drawn the way the kit draws a leaderboard: the top three on
   position cards, everyone else in the table beneath, and the signed-in
   player's own row marked so they can find themselves without reading the
   whole list. The race and volume boards share it; only the rows differ.

   A name opens the same player card as the live feeds — in the table, the
   hover card; on a podium card, whose sheen clips anything that overflows,
   the full statistics modal instead. */

export type BoardRow = {
  id: string;
  nickname: string;
  isHidden: boolean;
  value: string;
  prize: string | null;
};

const PODIUM_LABEL = ["1ST", "2ND", "3RD"];

export function Board({
  title,
  note,
  live,
  rows,
  valueLabel,
  loading,
  error,
  emptyTitle,
  emptyBody,
  selfId,
}: {
  title: string;
  note?: ReactNode;
  live?: boolean;
  rows: BoardRow[];
  valueLabel: string;
  loading?: boolean;
  error: string | null;
  emptyTitle: string;
  emptyBody: string;
  selfId?: string;
}) {
  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="text-h6 font-extrabold lg:text-h5">{title}</h2>
        {live && (
          <span className="flex items-center gap-[6px] text-[11px] font-bold uppercase tracking-[0.08em] text-fg-subtle">
            <span className="relative flex h-[8px] w-[8px]">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
              <span className="relative inline-flex h-[8px] w-[8px] rounded-full bg-success" />
            </span>
            Live
          </span>
        )}
      </div>

      {note && <p className="text-[12px] leading-[1.6] text-fg-muted">{note}</p>}

      {error && (
        <Panel>
          <SectionError title={`${title} didn’t load`} body={error} />
        </Panel>
      )}

      {!error && loading && rows.length === 0 && (
        <Panel>
          <RowsSkeleton rows={5} />
        </Panel>
      )}

      {!error && !loading && rows.length === 0 && (
        <Panel>
          <SectionEmpty icon="trophy" title={emptyTitle} body={emptyBody} />
        </Panel>
      )}

      {podium.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          {podium.map((row, index) => {
            const own = row.id === selfId;

            return (
              <div
                key={row.id}
                className={cn(
                  "surface-sheen relative overflow-hidden rounded-lg ring-1",
                  index === 0 || own ? "ring-brand/60" : "ring-border",
                )}
              >
                <span
                  className={cn(
                    "absolute left-1/2 top-2 z-10 -translate-x-1/2 rounded-rounded px-2 py-[2px] text-[9px] font-black",
                    index === 0 ? "bg-brand text-white" : "bg-elevation-3 text-fg-muted",
                  )}
                >
                  {PODIUM_LABEL[index]}
                </span>
                {own && (
                  <span className="absolute right-2 top-2 z-10">
                    <Chip tone="subtle">You</Chip>
                  </span>
                )}
                <div className="flex items-center justify-center px-4 pb-4 pt-7 text-[15.75px] lg:text-[17.5px]">
                  <PodiumName row={row} />
                </div>
                <div className="border-y border-border py-2 text-center">
                  <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-fg-subtle">{valueLabel}</p>
                  <p className="text-[12px] font-bold tabular-nums">{row.value}</p>
                </div>
                <p className="py-5 text-center text-h5 font-black text-brand-bold lg:text-h4">{row.prize ?? "—"}</p>
              </div>
            );
          })}
        </div>
      )}

      {rest.length > 0 && (
        <Panel className="overflow-hidden">
          <div role="region" aria-label={title} tabIndex={0} className="scrollbar-none overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse">
              <thead>
                <tr className="h-[42px] border-b border-border bg-elevation-2 text-[12px] font-bold leading-[1.6] text-fg-subtle">
                  <th className="w-12 pl-[14px] text-left">#</th>
                  <th className="text-left">PLAYER</th>
                  <th className="text-right">{valueLabel}</th>
                  <th className="pr-[14px] text-right">PRIZE</th>
                </tr>
              </thead>
              <tbody>
                {rest.map((row, index) => {
                  const own = row.id === selfId;

                  return (
                    <tr
                      key={row.id}
                      className={cn(
                        "h-[51px] border-b border-border text-[14px] leading-[1.6] transition-colors last:border-0 hover:bg-elevation-3",
                        own ? "bg-elevation-3" : "bg-elevation-2",
                      )}
                    >
                      <td
                        className={cn(
                          "pl-[14px] font-bold tabular-nums",
                          own && "shadow-[inset_3px_0_0_0_var(--color-brand)]",
                        )}
                      >
                        {index + 4}.
                      </td>
                      <td>
                        <span className="flex items-center gap-2">
                          <PlayerCell name={row.nickname} accountId={row.isHidden ? undefined : row.id} />
                          {own && <Chip tone="subtle">You</Chip>}
                        </span>
                      </td>
                      <td className="text-right font-bold tabular-nums">{row.value}</td>
                      <td className="pr-[14px] text-right font-bold text-brand-fg-accent">{row.prize ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </section>
  );
}

function PodiumName({ row }: { row: BoardRow }) {
  const [open, setOpen] = useState(false);

  if (row.isHidden) {
    return (
      <span className="inline-flex items-center gap-2 font-bold text-fg-on-disabled">
        <Icon name="incognito" size={22} className="text-fg-subtle" />
        {row.nickname}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="See this player's statistics"
        className="inline-flex cursor-pointer items-center gap-2 font-bold transition-colors hover:text-brand-fg-accent"
      >
        <Avatar name={row.nickname} size={22} glow />
        {row.nickname}
      </button>
      <PlayerStatsModal accountId={open ? row.id : null} name={row.nickname} onClose={() => setOpen(false)} />
    </>
  );
}
